import React, { useState, Dispatch, useEffect } from 'react'
import { useParams, useNavigate } from "react-router-dom"
import { Row, Col, Select, Input, Table, Button, Tabs, Spin } from "antd"
import { FileImageOutlined, EditOutlined, LoadingOutlined } from '@ant-design/icons'
import { getTrayFromBp } from '../../api/trayTable/TrayTableMethods'
import Fuse from 'fuse.js'
import EditModal from "./EditModal"
import PhotoModal from "./PhotoModal"
import "./editTable.css"
import { useUserContext } from "../../userContext"

interface DataType {
  _id: string,
  key: number,
  trayNumber: string,
  dateCreated: string,
  SKUs: { id: string, type: string },
  quantity: string
}


const EditTable = (props: { data: any, setData: Dispatch<any>, check: boolean, selectedRowKeys: string[], setSelectedRowKeys: Dispatch<any[]> }) => {
  const { bpId, sku, status } = useParams()
  const [modalPhoto, setModalPhoto] = useState<boolean>(false)
  const [modalEdit, setModalEdit] = useState<boolean>(false)
  const [selectedData, setSelectedData] = useState<DataType | undefined>(undefined)
  const [selectedType, setSelectedType] = useState<string>('trayNumber')
  const [loading, setLoading] = useState<boolean>(false)
  const [keyStatus, setKeyStatus] = useState<string>(status ? "2" : "1")
  const [results, setResults] = useState<any[]>(props.data)
  const [fuse, setFuse] = useState<any>(undefined)
  const [empty, setEmpty] = useState<boolean>(true)
  const [pageNumber, setPage] = useState<number>(1)
  const { Option } = Select;
  const { data, setData, selectedRowKeys, setSelectedRowKeys } = props
  const { user } = useUserContext()

  const navigate = useNavigate()

  function padTo2Digits(num) {
    return num.toString().padStart(2, '0');
  }

  function formatDate(date: Date) {
    if (date) {
      return (
        [
          padTo2Digits(date.getDate()),
          padTo2Digits(date.getMonth() + 1),
          date.getFullYear(),
        ].join('/') +
        ' ' +
        [
          padTo2Digits(date.getHours()),
          padTo2Digits(date.getMinutes()),
        ].join(':')
      );
    }
  }

  const handleRetake = (record) => {
    navigate("/scanning/" + bpId + "/" + sku + "/" + record.trayNumber, { state: { ...record, progression: [0, 100], bpId: bpId } })
  }

  const col = [
    {
      title: "N Plateau",
      dataIndex: "trayNumber",
      key: 'trayNumber',
      render: (text: number) => <span className='tray-number'>{text}</span>
    },
    {
      title: "Ajouté le",
      dataIndex: "createdOn",
      key: "dateCreated",
      render: (text: string) => <span>{formatDate(new Date(text))}</span>
    },
    {
      title: "OLPN",
      dataIndex: "OLPN",
      render: (text) => {
        return <span style={text?.OLPN ? { fontWeight: "bold" } : { fontStyle: "italic" }}>{text?.OLPN ? text?.OLPN : "Colis en cours"}</span>
      }
    },
    {
      title: "Quantité",
      dataIndex: "skuList",
      key: 'quantity',
      render: (list: string | any[]) => (
        <div className='quantity-div'>
          <span>{list.length}</span>
        </div>
      )
    },
    {
      title: "Action",
      key: 'action',
      render: (_text: string, record: DataType) => (
        <>
          <Button icon={<FileImageOutlined />} className='button-photo' onClick={() => handleOpenPhoto(record)}>
            <span className='action-text'>voir la photo</span>
          </Button>
          <br />
          {keyStatus !== "1" ?
            <Button icon={<EditOutlined />} className='button-edit' onClick={() => handleRetake(record)}>
              <span className='action-text'>Reprendre</span>
            </Button>
            : user?.group === 'admin' ?
              <Button icon={<EditOutlined />} className='button-edit' onClick={() => handleEditPhoto(record)}>
                <span className='action-text'>éditer</span>
              </Button>
              : <></>
          }
        </>
      )
    }
  ]

  const [colTable, setColTable] = useState(col)

  const handleOpenPhoto = (record: DataType) => {
    setSelectedData(record)
    setModalPhoto(true)
  }

  const handleEditPhoto = (record: DataType) => {
    setSelectedData(record)
    setModalEdit(true)
  }

  const handleChange = async (key: string) => {
    setKeyStatus(key)
  }

  const handleInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const result = fuse?.search(event.target.value)
    let list: any = []
    list.push(result.map((elem: { item: any }) => elem.item))
    setResults(list[0])
    if (event.target.value.length === 0)
      setEmpty(true)
    else setEmpty(false)
  }

  const handleSelect = async (event: string) => {
    if (event === "OLPN")
      event = "OLPN.OLPN"
    setSelectedType(event)
    setFuse(new Fuse(data, { keys: [event], distance: 1, threshold: 0.0 }))
  }

  const makeCall = async () => {
    if (bpId) {
      setLoading(true)
      let data = await getTrayFromBp(bpId, keyStatus === "1" ? "SUCCESS" : "PENDING")
      setLoading(false)
      setData(data)
    }
  }

  useEffect(() => {
    setFuse(new Fuse(data, { keys: [selectedType], distance: 1, threshold: 0.0 }))
  }, [data])

  useEffect(() => {
    makeCall()
  }, [selectedType, keyStatus])

  const handleCheckbox = (record, selected) => {
    if (selected) {
      setSelectedRowKeys((keys: any[]) => [...keys, record._id]);
    } else {
      setSelectedRowKeys((keys: string[]) => {
        const index = keys.indexOf(record._id);
        return [...keys.slice(0, index), ...keys.slice(index + 1)];
      });
    }
  };

  const SuccessList = () => (
    <Spin spinning={loading} indicator={<LoadingOutlined />}>
      <Table dataSource={results && !empty ? results : data} columns={colTable} bordered
        rowKey={(record) => record._id}
        rowSelection={{
          selectedRowKeys,
          columnTitle: "",
          hideSelectAll: true,
          onSelect: handleCheckbox,
        }}
        pagination={{
          itemRender(_page, type, element) {
            if (type === 'prev') return <Button className='rounded-button'>Précédent</Button>
            if (type === 'next') return <Button className='rounded-button'>Suivant</Button>
            return element
          },
          onChange: (page) => setPage(page),
          current: pageNumber
        }}
      />
    </Spin>
  )

  const ErrorList = () => (
    <Spin spinning={loading} indicator={<LoadingOutlined />}>
      <Table dataSource={results && !empty ? results : data} columns={col} bordered
        rowKey={(record) => record._id}
        rowSelection={{
          selectedRowKeys,
          columnTitle: "",
          hideSelectAll: true,
          onSelect: handleCheckbox,
        }}
        pagination={{
          itemRender(_page, type, element) {
            if (type === 'prev') return <Button className='rounded-button'>Précédent</Button>
            if (type === 'next') return <Button className='rounded-button'>Suivant</Button>
            return element
          }
        }}
      />
    </Spin>
  )

  return (
    <>
      <PhotoModal setModalPhoto={setModalPhoto} photoModal={modalPhoto} data={selectedData} setSelectedData={setSelectedData} bpId={bpId} />
      <EditModal setModalEdit={setModalEdit} editModal={modalEdit} data={selectedData} makeCall={makeCall} />
      <Row className='edit-table-row'>
        <Col xs={12}></Col>
        <Col xs={6}>
          <Select onChange={(event) => handleSelect(event)} defaultValue={"trayNumber"} className='edit-select'>
            <Option value="trayNumber">N° Plateau</Option>
            <Option value="OLPN">OLPN</Option>
          </Select>
        </Col>
        <Col xs={6}>
          <Input.Search allowClear onChange={(event) => { handleInput(event) }} placeholder='Recherchez un SKU, n plateau...' />
        </Col>
        <Col xs={24} style={{ paddingTop: "1rem" }}>
          <Tabs defaultActiveKey={status ? "2" : "1"} onChange={(key: string) => { handleChange(key) }} items={[
            { label: "plateaux validés", key: "1", children: <SuccessList /> },
            { label: "plateaux en attente", key: "2", children: <ErrorList /> },
          ]}>
          </Tabs>
        </Col>
      </Row >
    </>
  )
}

export default EditTable